// Offline basemap regions.
//
// Phase 2: download a region's .pmtiles file from the Supabase `offline-maps`
// public bucket to Capacitor Filesystem, persist a local registry, and expose
// a local URI that MapLibre + pmtiles can read offline.
//
// Pipeline:
//   1. Admin uploads `mongolia.pmtiles` (or other region files) to the
//      `offline-maps` public bucket once. The public CDN URL is what we
//      download from.
//   2. Traveler taps "Download Mongolia map" (UI in Phase 3) which calls
//      `downloadRegion()`. Native iOS URLSession streams the file to
//      Application Support and reports progress events.
//   3. On completion, the local URI is registered in localStorage so future
//      app launches know the region is present without re-checking disk.
//   4. `getLocalRegionUri()` returns the on-disk URI when present; basemap.ts
//      uses it to construct a `pmtiles://` style source.
//
// Web fallback: when not running under Capacitor, `downloadRegion()` is a
// no-op that immediately throws — the offline map is a native-only feature.

import { Capacitor } from "@capacitor/core";
import { Filesystem, Directory } from "@capacitor/filesystem";
import type { PluginListenerHandle } from "@capacitor/core";

const REGISTRY_KEY = "roava:offlineMapRegions:v1";
const SUPABASE_PUBLIC_BUCKET_BASE =
  "https://jdawqgtbozntxdzsvzse.supabase.co/storage/v1/object/public/offline-maps";

export type RegionId = string;

export type RegionDescriptor = {
  id: RegionId;
  label: string;
  /** Flag or short emoji shown next to the region name. */
  flag: string;
  /** Object name inside the `offline-maps` bucket. */
  objectName: string;
  /** Approximate download size in megabytes, shown in UI before download. */
  approxMb: number;
  /** Center + zoom used when no other camera state is known. */
  center: [number, number];
  initialZoom: number;
  /** False if the pmtiles file hasn't been built/uploaded yet. */
  built: boolean;
};

export const REGIONS: Record<RegionId, RegionDescriptor> = {
  mongolia: {
    id: "mongolia",
    label: "Mongolia",
    flag: "🇲🇳",
    objectName: "mongolia.pmtiles",
    approxMb: 250,
    center: [103.8333, 46.8625],
    initialZoom: 5,
    built: true,
  },
  patagonia: {
    id: "patagonia",
    label: "Patagonia",
    flag: "🇦🇷",
    objectName: "patagonia.pmtiles",
    approxMb: 21,
    center: [-72.5, -49],
    initialZoom: 5,
    built: true,
  },
  mexico: {
    id: "mexico",
    label: "Mexico",
    flag: "🇲🇽",
    objectName: "mexico.pmtiles",
    approxMb: 51,
    center: [-102, 23.5],
    initialZoom: 5,
    built: true,
  },
  usa: {
    id: "usa",
    label: "USA (CONUS)",
    flag: "🇺🇸",
    objectName: "usa.pmtiles",
    approxMb: 150,
    center: [-98.5, 39.5],
    initialZoom: 4,
    built: true,
  },
};

export function listRegions(): RegionDescriptor[] {
  return Object.values(REGIONS);
}


type RegistryEntry = {
  /** Capacitor.convertFileSrc(localUri) — directly fetchable by MapLibre (legacy; not used for pmtiles anymore). */
  servableUrl: string;
  /** Original on-disk URI, used for delete and diagnostics. */
  localUri: string;
  /** Relative path within Directory.Data — used for partial-range reads via Filesystem.readFile. */
  storagePath: string;
  bytes: number;
  /** Expected total file size derived from the pmtiles header (tile_data_off + tile_data_len). May be 0 if header parse failed. */
  expectedBytes?: number;
  downloadedAt: string;
  sourceUrl: string;
};

type Registry = Record<string, RegistryEntry>;

// Tiny pub-sub so map components can swap style in-place when a region
// becomes available or is deleted, without remounting.
type RegionChangeListener = (id: RegionId) => void;
const listeners = new Set<RegionChangeListener>();
export function subscribeRegionChanges(fn: RegionChangeListener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
function emitRegionChanged(id: RegionId) {
  for (const fn of listeners) {
    try {
      fn(id);
    } catch {
      /* listener errors are non-fatal */
    }
  }
}

function readRegistry(): Registry {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(REGISTRY_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return typeof parsed === "object" && parsed !== null ? (parsed as Registry) : {};
  } catch {
    return {};
  }
}

function writeRegistry(reg: Registry): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(REGISTRY_KEY, JSON.stringify(reg));
  } catch {
    /* localStorage quota — non-fatal */
  }
}

export function getRemoteRegionUrl(id: RegionId): string {
  const region = REGIONS[id];
  return `${SUPABASE_PUBLIC_BUCKET_BASE}/${region.objectName}`;
}

/**
 * Returns the local servable URL for a region if it has been downloaded.
 * Kept for back-compat. New callers should prefer getLocalRegionPath()
 * because pmtiles is now served via Filesystem.readFile, not capacitor://.
 */
export function getLocalRegionUri(id: RegionId): string | null {
  if (!Capacitor.isNativePlatform()) return null;
  const reg = readRegistry();
  return reg[id]?.servableUrl ?? null;
}

/**
 * Returns the on-disk relative path (Directory.Data) for a downloaded region,
 * or null if not present. Used by the Capacitor pmtiles Source.
 */
export function getLocalRegionPath(id: RegionId): string | null {
  if (!Capacitor.isNativePlatform()) return null;
  const reg = readRegistry();
  const entry = reg[id];
  if (!entry) return null;
  // Older registry entries (pre-Capacitor source) may not have storagePath —
  // derive it from the region descriptor as a fallback.
  if (entry.storagePath) return entry.storagePath;
  const region = REGIONS[id];
  return region ? `offline-maps/${region.objectName}` : null;
}

export type RegionStatus =
  | { state: "missing"; canDownload: boolean }
  | {
      state: "present";
      bytes: number;
      expectedBytes?: number;
      downloadedAt: string;
      servableUrl: string;
      storagePath: string;
    };

export function getRegionStatus(id: RegionId): RegionStatus {
  const reg = readRegistry();
  const entry = reg[id];
  if (entry) {
    return {
      state: "present",
      bytes: entry.bytes,
      expectedBytes: entry.expectedBytes,
      downloadedAt: entry.downloadedAt,
      servableUrl: entry.servableUrl,
      storagePath: entry.storagePath || `offline-maps/${REGIONS[id]?.objectName ?? ""}`,
    };
  }
  return {
    state: "missing",
    canDownload: Capacitor.isNativePlatform(),
  };
}


export type DownloadProgress = {
  bytes: number;
  totalBytes: number | null;
  percent: number | null;
};

export type DownloadController = {
  promise: Promise<RegistryEntry>;
  cancel: () => Promise<void>;
};

/**
 * Stream a region's pmtiles file to Application Support. Progress events fire
 * via the optional `onProgress` callback. Native iOS URLSession does the
 * actual transfer — it survives app backgrounding within iOS's normal
 * background-task allowance.
 */
export function downloadRegion(
  id: RegionId,
  onProgress?: (p: DownloadProgress) => void,
): DownloadController {
  if (!Capacitor.isNativePlatform()) {
    return {
      promise: Promise.reject(new Error("Offline regions are only available in the iOS app.")),
      cancel: async () => {},
    };
  }

  const region = REGIONS[id];
  if (!region) {
    return {
      promise: Promise.reject(new Error(`Unknown region: ${id}`)),
      cancel: async () => {},
    };
  }
  if (!region.built) {
    return {
      promise: Promise.reject(new Error(`The ${region.label} map file isn't built yet — coming soon.`)),
      cancel: async () => {},
    };
  }

  const sourceUrl = getRemoteRegionUrl(id);
  const path = `offline-maps/${region.objectName}`;

  let progressListener: PluginListenerHandle | null = null;
  let cancelled = false;

  const promise = (async (): Promise<RegistryEntry> => {
    // Make sure the parent directory exists so the download doesn't fail.
    try {
      await Filesystem.mkdir({
        path: "offline-maps",
        directory: Directory.Data,
        recursive: true,
      });
    } catch {
      /* already exists — Filesystem.mkdir errors instead of being a no-op */
    }

    if (onProgress) {
      progressListener = await Filesystem.addListener("progress", (e: any) => {
        const bytes = Number(e?.bytes ?? 0);
        const totalBytes = e?.contentLength ? Number(e.contentLength) : null;
        const percent =
          totalBytes && totalBytes > 0 ? Math.min(100, (bytes / totalBytes) * 100) : null;
        onProgress({ bytes, totalBytes, percent });
      });
    }

    try {
      // @capacitor/filesystem v8: downloadFile uses iOS URLSession when on
      // native, with `progress: true` enabling progress events.
      const result = await (Filesystem as any).downloadFile({
        url: sourceUrl,
        path,
        directory: Directory.Data,
        progress: true,
        recursive: true,
      });

      if (cancelled) {
        await Filesystem.deleteFile({ path, directory: Directory.Data }).catch(() => {});
        throw new Error("Download cancelled");
      }

      // Confirm the file actually landed and capture its size.
      const stat = await Filesystem.stat({ path, directory: Directory.Data });
      const bytes = Number((stat as any).size ?? 0);
      const localUri: string = String(result?.path ?? (stat as any).uri ?? "");
      if (!localUri) {
        throw new Error("Download finished but no local URI returned by Filesystem");
      }

      // convertFileSrc rewrites a `file://...` URI to a `capacitor://...`
      // URL that the WebView is allowed to fetch.
      const servableUrl = Capacitor.convertFileSrc(localUri);

      // Read the pmtiles header (first 127 bytes) to capture the expected
      // total file size so the UI can detect partial/truncated downloads.
      let expectedBytes: number | undefined;
      try {
        expectedBytes = await readPmtilesExpectedBytes(path);
      } catch {
        /* non-fatal — verify UI will just skip the size check */
      }

      const entry: RegistryEntry = {
        servableUrl,
        localUri,
        storagePath: path,
        bytes,
        expectedBytes,
        downloadedAt: new Date().toISOString(),
        sourceUrl,
      };

      const reg = readRegistry();
      reg[id] = entry;
      writeRegistry(reg);
      emitRegionChanged(id);

      // Best-effort console marker so traveler-side logs are clear.
      // eslint-disable-next-line no-console
      console.info(
        `[offline-maps] region=${id} ready bytes=${bytes} url=${servableUrl.slice(0, 64)}…`,
      );
      return entry;
    } finally {
      if (progressListener) {
        try {
          await progressListener.remove();
        } catch {
          /* ignore */
        }
      }
    }
  })();

  return {
    promise,
    cancel: async () => {
      cancelled = true;
      try {
        await Filesystem.deleteFile({ path, directory: Directory.Data });
      } catch {
        /* not yet on disk — fine */
      }
    },
  };
}

export async function deleteRegion(id: RegionId): Promise<void> {
  const reg = readRegistry();
  const entry = reg[id];
  if (!entry) return;

  if (Capacitor.isNativePlatform()) {
    const region = REGIONS[id];
    const path = `offline-maps/${region.objectName}`;
    try {
      await Filesystem.deleteFile({ path, directory: Directory.Data });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn(`[offline-maps] delete file failed (continuing)`, err);
    }
  }

  delete reg[id];
  writeRegistry(reg);
  emitRegionChanged(id);
}

/**
 * Best-effort total bytes used by all downloaded regions. UI in Phase 3
 * uses this for the "Storage used" line.
 */
export function getTotalRegionBytes(): number {
  const reg = readRegistry();
  return Object.values(reg).reduce((sum, e) => sum + (e.bytes || 0), 0);
}

// --- pmtiles header helpers --------------------------------------------------

function base64ToUint8Array(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

/**
 * Read the first 127 bytes of a pmtiles file and return tile_data_off +
 * tile_data_len, i.e. the expected total file size. pmtiles v3 spec.
 */
async function readPmtilesExpectedBytes(path: string): Promise<number | undefined> {
  if (!Capacitor.isNativePlatform()) return undefined;
  const res = await Filesystem.readFile({
    path,
    directory: Directory.Data,
    offset: 0,
    length: 127,
  });
  if (typeof res.data !== "string") return undefined;
  const bytes = base64ToUint8Array(res.data);
  if (bytes.length < 127) return undefined;
  const magic = String.fromCharCode(...bytes.slice(0, 7));
  if (magic !== "PMTiles") return undefined;
  const dv = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const tileDataOff = Number(dv.getBigUint64(8 + 8 * 6, true));
  const tileDataLen = Number(dv.getBigUint64(8 + 8 * 7, true));
  if (!Number.isFinite(tileDataOff) || !Number.isFinite(tileDataLen)) return undefined;
  return tileDataOff + tileDataLen;
}

export type RegionVerifyResult =
  | { ok: true; bytes: number; expectedBytes?: number }
  | { ok: false; reason: string; bytes?: number; expectedBytes?: number };

/**
 * Quick health check on a downloaded region: confirms the on-disk file
 * starts with the pmtiles magic and (if we know the expected size) that
 * the download wasn't truncated.
 */
export async function verifyRegion(id: RegionId): Promise<RegionVerifyResult> {
  if (!Capacitor.isNativePlatform()) {
    return { ok: false, reason: "Verification only works on the iOS app." };
  }
  const reg = readRegistry();
  const entry = reg[id];
  if (!entry) return { ok: false, reason: "Region not downloaded." };
  const path = entry.storagePath || `offline-maps/${REGIONS[id]?.objectName ?? ""}`;
  try {
    const stat = await Filesystem.stat({ path, directory: Directory.Data });
    const bytes = Number((stat as any).size ?? 0);
    const expectedBytes =
      entry.expectedBytes ?? (await readPmtilesExpectedBytes(path).catch(() => undefined));

    const head = await Filesystem.readFile({
      path,
      directory: Directory.Data,
      offset: 0,
      length: 8,
    });
    if (typeof head.data !== "string") {
      return { ok: false, reason: "Couldn't read file header.", bytes, expectedBytes };
    }
    const headBytes = base64ToUint8Array(head.data);
    const magic = String.fromCharCode(...headBytes.slice(0, 7));
    if (magic !== "PMTiles") {
      return { ok: false, reason: `Not a pmtiles archive (magic=${JSON.stringify(magic)}).`, bytes, expectedBytes };
    }
    if (headBytes[7] !== 3) {
      return { ok: false, reason: `Unsupported pmtiles version ${headBytes[7]}.`, bytes, expectedBytes };
    }
    if (expectedBytes && bytes < expectedBytes) {
      return {
        ok: false,
        reason: `Download incomplete: ${bytes} of ${expectedBytes} bytes on disk.`,
        bytes,
        expectedBytes,
      };
    }
    if (expectedBytes && !entry.expectedBytes) {
      entry.expectedBytes = expectedBytes;
      reg[id] = entry;
      writeRegistry(reg);
    }
    return { ok: true, bytes, expectedBytes };
  } catch (err) {
    return { ok: false, reason: err instanceof Error ? err.message : String(err) };
  }
}
